import { useEffect, useState } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { grupoService, subGrupoService } from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import PageContainer from "../../components/PageContainer";

const Subgrupo = () => {
  const [loading, setLoading] = useState(false);
  const [subGrupos, setSubGrupos] = useState([]);
  const [gruposDisponiveis, setGruposDisponiveis] = useState([]);
  const [showSubGrupoForm, setShowSubGrupoForm] = useState(false);
  const [editingSubGrupo, setEditingSubGrupo] = useState(null);
  const [subGrupoForm, setSubGrupoForm] = useState({ nome: "", grupo: { id: "" } });
  const [subGrupoSearchTerm, setSubGrupoSearchTerm] = useState("");
  const [paginacaoSubGrupo, setPaginacaoSubGrupo] = useState({
    paginaAtual: 1,
    itensPorPagina: 6,
  });

  const { showSuccess, showError } = useNotification();
  const confirmDialog = useConfirmDialog();

  useEffect(() => {
    loadSubGrupos();
    loadGruposDisponiveis();
  }, []);

  const loadSubGrupos = async (nome = "") => {
    setLoading(true);
    try {
      const data = await subGrupoService.listarSubGrupo(nome || undefined);
      setSubGrupos(data || []);
      setPaginacaoSubGrupo((prev) => ({ ...prev, paginaAtual: 1 }));
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const loadGruposDisponiveis = async () => {
    try {
      const data = await grupoService.listarGrupo(undefined);
      setGruposDisponiveis(data || []);
    } catch (err) {
    }
  };

  const handleSubGrupoSearch = (e) => {
    const value = e.target.value;
    setSubGrupoSearchTerm(value);

    const timeoutId = setTimeout(() => {
      loadSubGrupos(value.trim());
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  const totalSubGrupos = subGrupos.length;
  const totalPaginasSubGrupo = Math.ceil(
    totalSubGrupos / paginacaoSubGrupo.itensPorPagina
  );
  const indiceInicialSubGrupo =
    (paginacaoSubGrupo.paginaAtual - 1) * paginacaoSubGrupo.itensPorPagina;
  const indiceFinalSubGrupo =
    indiceInicialSubGrupo + paginacaoSubGrupo.itensPorPagina;
  const subGruposPaginados = subGrupos.slice(
    indiceInicialSubGrupo,
    indiceFinalSubGrupo
  );

  const irParaPrimeiraPaginaSubGrupo = () => {
    setPaginacaoSubGrupo((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const irParaUltimaPaginaSubGrupo = () => {
    setPaginacaoSubGrupo((prev) => ({
      ...prev,
      paginaAtual: totalPaginasSubGrupo,
    }));
  };

  const irParaPaginaAnteriorSubGrupo = () => {
    setPaginacaoSubGrupo((prev) => ({
      ...prev,
      paginaAtual: Math.max(1, prev.paginaAtual - 1),
    }));
  };

  const irParaProximaPaginaSubGrupo = () => {
    setPaginacaoSubGrupo((prev) => ({
      ...prev,
      paginaAtual: Math.min(totalPaginasSubGrupo, prev.paginaAtual + 1),
    }));
  };

  const irParaPaginaSubGrupo = (numeroPagina) => {
    setPaginacaoSubGrupo((prev) => ({ ...prev, paginaAtual: numeroPagina }));
  };

  const gerarNumerosPaginasSubGrupo = () => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    let inicio = Math.max(
      1,
      paginacaoSubGrupo.paginaAtual - Math.floor(maxPaginasVisiveis / 2)
    );
    let fim = Math.min(totalPaginasSubGrupo, inicio + maxPaginasVisiveis - 1);

    if (fim - inicio < maxPaginasVisiveis - 1) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }

    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }

    return paginas;
  };

  const handleSubGrupoSubmit = async () => {
    if (!subGrupoForm.nome.trim()) {
      showError("Nome do subgrupo e obrigatorio");
      return;
    }

    if (!subGrupoForm.grupo.id) {
      showError("Grupo e obrigatorio");
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        nome: subGrupoForm.nome,
        grupo: { id: subGrupoForm.grupo.id },
      };

      if (editingSubGrupo) {
        await subGrupoService.atualizarSubGrupo(editingSubGrupo.id, dataToSend);
        showSuccess("Subgrupo atualizado com sucesso!");
      } else {
        await subGrupoService.cadastrarSubGrupo(dataToSend);
        showSuccess("Subgrupo criado com sucesso!");
      }

      await loadSubGrupos(subGrupoSearchTerm);
      handleSubGrupoCancel();
    } catch (err) {
      showError(err.message || "Erro ao salvar subgrupo");
    } finally {
      setLoading(false);
    }
  };

  const handleSubGrupoEdit = (item) => {
    const grupoId = item.grupo?.id || "";
    setEditingSubGrupo(item);
    setSubGrupoForm({
      nome: item.nome || "",
      grupo: { id: grupoId ? String(grupoId) : "" },
    });
    setShowSubGrupoForm(true);
  };

  const handleSubGrupoDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja excluir este subgrupo?",
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);

    try {
      await subGrupoService.deletarSubGrupo(id);
      showSuccess("Subgrupo excluido com sucesso!");
      await loadSubGrupos(subGrupoSearchTerm);
    } catch (err) {
      showError(err.message || "Erro ao excluir subgrupo");
    } finally {
      setLoading(false);
    }
  };

  const handleSubGrupoCancel = () => {
    setShowSubGrupoForm(false);
    setEditingSubGrupo(null);
    setSubGrupoForm({ nome: "", grupo: { id: "" } });
  };

  const handleSubGrupoNew = async () => {
    if (!gruposDisponiveis.length) {
      await loadGruposDisponiveis();
    }
    setEditingSubGrupo(null);
    setSubGrupoForm({ nome: "", grupo: { id: "" } });
    setShowSubGrupoForm(true);
  };

  const handleSubGrupoKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubGrupoSubmit();
    }
  };

  const getGrupoNome = (item) => {
    const grupoId = item?.grupo?.id || "";
    return (
      item?.grupo?.nome ||
      item?.grupoNome ||
      gruposDisponiveis.find((grupo) => grupo.id === grupoId)?.nome ||
      "-"
    );
  };

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Subgrupos</h1>
        <p className="text-gray-600">Gerencie os subgrupos utilizados no sistema</p>
      </div>

      <div>
        <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Pesquisar por nome..."
              value={subGrupoSearchTerm}
              onChange={handleSubGrupoSearch}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <button
            onClick={handleSubGrupoNew}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus size={20} />
            Novo Subgrupo
          </button>
        </div>

        {showSubGrupoForm && (
          <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
            <h3 className="text-lg font-semibold mb-4">
              {editingSubGrupo ? "Editar Subgrupo" : "Novo Subgrupo"}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="subgrupo-grupo"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Grupo *
                </label>
                <select
                  id="subgrupo-grupo"
                  value={subGrupoForm.grupo.id}
                  onChange={(e) =>
                    setSubGrupoForm({
                      ...subGrupoForm,
                      grupo: { id: e.target.value },
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecione um grupo</option>
                  {gruposDisponiveis.map((grupo) => (
                    <option key={grupo.id} value={grupo.id}>
                      {grupo.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="subgrupo-nome"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Nome *
                </label>
                <input
                  type="text"
                  id="subgrupo-nome"
                  value={subGrupoForm.nome}
                  onChange={(e) =>
                    setSubGrupoForm({
                      ...subGrupoForm,
                      nome: e.target.value,
                    })
                  }
                  onKeyDown={handleSubGrupoKeyPress}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Digite o nome do subgrupo"
                  maxLength={100}
                />
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={handleSubGrupoSubmit}
                disabled={loading || !subGrupoForm.nome.trim() || !subGrupoForm.grupo.id}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Save size={16} />
                {loading ? "Salvando..." : "Salvar"}
              </button>

              <button
                onClick={handleSubGrupoCancel}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <X size={16} />
                Cancelar
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow border">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="text-lg font-semibold">Lista de Subgrupos</h3>
            <span className="text-sm text-gray-500">
              {totalSubGrupos} registro(s) encontrado(s)
            </span>
          </div>

          {loading && subGrupos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Carregando subgrupos...</div>
          ) : subGrupos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {subGrupoSearchTerm ? "Nenhum subgrupo encontrado" : "Nenhum subgrupo cadastrado"}
            </div>
          ) : (
            <>
              <div>
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Grupo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subgrupo
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {subGruposPaginados.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{getGrupoNome(item)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{item.nome}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleSubGrupoEdit(item)}
                              disabled={loading}
                              className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 p-1 rounded transition-colors"
                              title="Editar"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleSubGrupoDelete(item.id)}
                              disabled={loading}
                              className="text-red-600 hover:text-red-900 disabled:text-gray-400 p-1 rounded transition-colors"
                              title="Excluir"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPaginasSubGrupo > 1 && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      Mostrando {indiceInicialSubGrupo + 1} ate {Math.min(indiceFinalSubGrupo, totalSubGrupos)} de {totalSubGrupos} registros
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={irParaPrimeiraPaginaSubGrupo}
                        disabled={paginacaoSubGrupo.paginaAtual === 1}
                        className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Primeira página"
                      >
                        <ChevronsLeft size={16} />
                      </button>

                      <button
                        onClick={irParaPaginaAnteriorSubGrupo}
                        disabled={paginacaoSubGrupo.paginaAtual === 1}
                        className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Página anterior"
                      >
                        <ChevronLeft size={16} />
                      </button>

                      <div className="flex space-x-1">
                        {gerarNumerosPaginasSubGrupo().map((numeroPagina) => (
                          <button
                            key={numeroPagina}
                            onClick={() => irParaPaginaSubGrupo(numeroPagina)}
                            className={`px-3 py-2 rounded-md text-sm font-medium ${
                              paginacaoSubGrupo.paginaAtual === numeroPagina
                                ? "bg-blue-600 text-white"
                                : "bg-white text-gray-500 border border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {numeroPagina}
                          </button>
                        ))}
                      </div>

                      <button
                        onClick={irParaProximaPaginaSubGrupo}
                        disabled={paginacaoSubGrupo.paginaAtual === totalPaginasSubGrupo}
                        className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Próxima página"
                      >
                        <ChevronRight size={16} />
                      </button>

                      <button
                        onClick={irParaUltimaPaginaSubGrupo}
                        disabled={paginacaoSubGrupo.paginaAtual === totalPaginasSubGrupo}
                        className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Última página"
                      >
                        <ChevronsRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default Subgrupo;
